import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, LabelList } from 'recharts';
import { CHAR_WIDTH, MIN_BAND, TICK_LINES, autoChartType, resolveChartType, splitLabel } from './chartLayout';
import './ResultsCharts.css';

export { autoChartType, resolveChartType, splitLabel };

/* Синхронизировано с --chart-* в theme.css (hex для совместимости с Recharts) */
export const COLORS = ['#6a4cd6', '#18b4c8', '#f2795b', '#f2b33d', '#a48ff0', '#0e8496'];

export const CHART_TYPES = [
  { value: 'bar', label: 'Вертикальная' },
  { value: 'horizontal', label: 'Горизонтальная' },
  { value: 'pie', label: 'Круговая' },
];

/** Ширина элемента; пересчитывается при изменении размера. */
export function useElementWidth() {
  const [el, setEl] = useState(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return [setEl, width];
}

// Подпись под столбцом: переносится по ширине столбца, не больше 3 строк, полный текст — во всплывающей подсказке
const WrappedTick = ({ x, y, payload, band, scale = 1 }) => {
  const maxChars = Math.max(3, Math.floor((band - 6) / (CHAR_WIDTH * scale)));
  const lines = splitLabel(payload.value, maxChars, TICK_LINES);
  const size = Math.round(12 * scale);
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{payload.value}</title>
      {lines.map((line, index) => (
        <text key={index} x={0} y={0} dy={size + 4 + index * (size + 2)} textAnchor="middle" fill="var(--color-text-secondary)" fontSize={`${size}px`}>
          {line}
        </text>
      ))}
    </g>
  );
};

/** Вертикальная столбчатая диаграмма. При большом числе столбцов не сжимает подписи, а прокручивается вбок.
 *  numericAxis — короткие числовые подписи (распределение чисел): всё в ширину, лишние подписи пропускаются.
 *  singleColor — один цвет (распределение чисел: цвета там ничего не значат).
 *  fontScale — крупнее подписи на большом экране и проекторе. */
export function VerticalBarChart({ data, dataKey = 'text', height, allowMultiple = false, showPercent = true, valueLabel = 'Количество ответов', numericAxis = false, singleColor = false, fontScale = 1 }) {
  const [ref, width] = useElementWidth();
  const count = data.length || 1;
  const fontSize = Math.round(12 * fontScale);
  const yAxisWidth = Math.round(36 * fontScale);
  const plotWidth = Math.max(width - yAxisWidth - 16, 0);
  const minBand = MIN_BAND * fontScale;
  const fits = numericAxis || plotWidth / count >= minBand;
  const chartWidth = fits ? width : yAxisWidth + 16 + count * minBand;
  const band = (chartWidth - yAxisWidth - 16) / count;
  const tickLines = numericAxis ? 1 : Math.min(TICK_LINES, Math.max(...data.map((d) => splitLabel(d[dataKey], Math.max(3, Math.floor((band - 6) / (CHAR_WIDTH * fontScale))), TICK_LINES).length)));
  const xAxisHeight = fontSize + 8 + tickLines * (fontSize + 2);

  return (
    <div className="rc-vbar" ref={ref} style={{ height }}>
      {width > 0 && (
        <div className="rc-vbar__scroll">
          <div style={{ width: chartWidth, height: fits ? height : height - 12 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: Math.round(22 * fontScale), right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
                {numericAxis ? (
                  <XAxis dataKey={dataKey} interval="preserveStartEnd" minTickGap={8} height={xAxisHeight} tick={{ fontSize, fill: 'var(--color-text-secondary)' }} stroke="var(--color-border-strong)" />
                ) : (
                  <XAxis dataKey={dataKey} interval={0} height={xAxisHeight} tick={<WrappedTick band={band} scale={fontScale} />} stroke="var(--color-border-strong)" />
                )}
                <YAxis width={yAxisWidth} allowDecimals={false} tick={{ fontSize, fill: 'var(--color-text-muted)' }} stroke="var(--color-border-strong)" />
                <Tooltip
                  cursor={{ fill: 'rgba(106, 76, 214, 0.08)' }}
                  formatter={(value, name, props) => {
                    const pct = props.payload.percentage;
                    if (allowMultiple || !showPercent || pct == null) return [`${value}`, valueLabel];
                    return [`${value} (${pct.toFixed(1)}%)`, valueLabel];
                  }}
                />
                <Bar dataKey="count" fill={COLORS[0]} maxBarSize={Math.round(120 * fontScale)} radius={[4, 4, 0, 0]}>
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={singleColor ? COLORS[0] : COLORS[index % COLORS.length]} />
                  ))}
                  {band >= 18 && <LabelList dataKey="count" position="top" fontSize={Math.round(13 * fontScale)} fontWeight={700} fill="var(--color-text)" />}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// Ниже этой высоты строки уже не читаются — тогда список прокручивается
const MIN_ROW_HEIGHT = 15;

/** Горизонтальные полосы (и рейтинг тем): подписи слева в 2 строки, значение справа.
 *  stacked — подпись над полосой во всю ширину (телефон: длинные варианты читаются целиком).
 *  fitHeight — уместить все строки в заданную высоту (встраиваемый вид). */
export function HorizontalBars({ options, ranking = false, allowMultiple = false, compact = false, stacked = false, fitHeight = null }) {
  const max = ranking ? (options[0]?.count || 1) : 100;
  const count = options.length || 1;
  let density = options.length > 12 ? 'dense' : '';
  let style;
  if (fitHeight) {
    const rowHeight = Math.max(fitHeight / count, MIN_ROW_HEIGHT);
    if (rowHeight < 24) density = 'tiny';
    else if (rowHeight < 38) density = 'dense';
    else density = '';
    style = {
      height: Math.max(fitHeight, count * MIN_ROW_HEIGHT),
      display: 'grid',
      gridTemplateRows: `repeat(${count}, minmax(0, ${Math.min(rowHeight, 64)}px))`,
      alignContent: 'center',
      gap: 0,
    };
  }
  return (
    <div
      className={`rc-hbars${compact ? ' rc-hbars--compact' : ''}${stacked ? ' rc-hbars--stacked' : ''}${density ? ` rc-hbars--${density}` : ''}${fitHeight ? ' rc-hbars--fit' : ''}`}
      style={style}
    >
      {options.map((option, idx) => {
        let marker = null;
        if (ranking) marker = `${idx + 1}`;
        const width = ranking ? (option.count / max) * 100 : option.percentage;
        return (
          <div key={option.id ?? idx} className="rc-hbar">
            <div className="rc-hbar__label" title={option.text}>
              {marker && <span className="rc-hbar__marker">{marker}</span>}
              <span className="rc-hbar__text">{option.text}</span>
            </div>
            <div className="rc-hbar__track">
              <div className="rc-hbar__fill" style={{ width: `${Math.max(0, Math.min(100, width))}%`, background: COLORS[idx % COLORS.length] }} />
            </div>
            <div className="rc-hbar__value">
              {ranking ? `${option.count} б.` : option.count}
              {!allowMultiple && !ranking && <span> · {option.percentage.toFixed(0)}%</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const PIE_LABEL_MIN_SHARE = 0.06;

/** Круговая диаграмма со своей легендой: легенда прокручивается и не наезжает на круг. */
export function PieWithLegend({ options, height }) {
  const [ref, width] = useElementWidth();
  const data = options.filter((o) => o.count > 0);
  const total = data.reduce((sum, o) => sum + o.count, 0) || 1;
  const stacked = width > 0 && width < 520;
  // В колонке круг занимает чуть больше половины высоты, рядом с легендой — всю
  const pieSize = height
    ? Math.max(120, Math.min(stacked ? height * 0.55 : height, stacked ? width : width * 0.55))
    : Math.min(stacked ? 300 : 380, stacked ? width : width * 0.55);

  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
    if (value / total < PIE_LABEL_MIN_SHARE) return null;
    const r = innerRadius + (outerRadius - innerRadius) * 0.62;
    const rad = Math.PI / 180;
    return (
      <text x={cx + r * Math.cos(-midAngle * rad)} y={cy + r * Math.sin(-midAngle * rad)} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
        {value}
      </text>
    );
  };

  return (
    <div className={`rc-pie${stacked ? ' rc-pie--stacked' : ''}`} ref={ref} style={height ? { height } : undefined}>
      {width > 0 && (
        <>
          <div className="rc-pie__chart" style={{ width: pieSize, height: pieSize }}>
            {data.length === 0 ? (
              <div className="rc-pie__empty">Нет ответов</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data} dataKey="count" nameKey="text" cx="50%" cy="50%" outerRadius="96%" labelLine={false} label={renderLabel} isAnimationActive={false}>
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[options.indexOf(entry) % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value} (${props.payload.percentage.toFixed(1)}%)`, name]} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <ul className="rc-pie__legend">
            {options.map((option, idx) => (
              <li key={option.id ?? idx} title={option.text} className={option.count === 0 ? 'is-empty' : ''}>
                <span className="rc-pie__swatch" style={{ background: COLORS[idx % COLORS.length] }} />
                <span className="rc-pie__name">{option.text}</span>
                <span className="rc-pie__value">{option.count} · {option.percentage.toFixed(0)}%</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

const TYPE_ICONS = {
  bar: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="3" y="11" width="4" height="10" rx="1" /><rect x="10" y="5" width="4" height="16" rx="1" /><rect x="17" y="14" width="4" height="7" rx="1" />
    </svg>
  ),
  horizontal: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="3" y="3" width="12" height="4" rx="1" /><rect x="3" y="10" width="18" height="4" rx="1" /><rect x="3" y="17" width="8" height="4" rx="1" />
    </svg>
  ),
  pie: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M11 2.05A10 10 0 1021.95 13H11V2.05z" /><path d="M13 2.05V11h8.95A10 10 0 0013 2.05z" opacity="0.6" />
    </svg>
  ),
};

/** Компактный переключатель типа диаграммы (кнопки с иконками). */
export function ChartTypeSwitch({ value, onChange, allowPie }) {
  return (
    <div className="rc-switch" role="radiogroup" aria-label="Тип диаграммы">
      {CHART_TYPES.filter((t) => allowPie || t.value !== 'pie').map((t) => (
        <button
          key={t.value}
          type="button"
          role="radio"
          aria-checked={value === t.value}
          className={value === t.value ? 'is-active' : ''}
          onClick={() => onChange(t.value)}
          title={t.label}
          aria-label={t.label}
        >
          {TYPE_ICONS[t.value]}
        </button>
      ))}
    </div>
  );
}
